import * as moment from "moment";
import { AppDb } from "sh-services/data/AppDb";
import { ECSConfigKey, ECSConfigService, InstrumentationService } from "sh-services";
import {
    IItemsInTimeRangeSyncState,
    IShiftDbEntity,
    IShiftEntity,
    ShiftEntity
    } from "sh-models";
import { IShiftDatabase } from "./IShiftDatabase";
import { Moment } from "moment";

/**
 * Shifts Database Accessor
 */
export class ShiftDatabase implements IShiftDatabase {
    private db: AppDb;

    constructor(db: AppDb) {
        if (ShiftDatabase.IsShiftsIndexedDbEnabled()) {
            this.db = db;
        }
    }
    /**
     * Fetch shift in a team with the specific ID
     */
    public async getShift(teamId: string, shiftId: string): Promise<IShiftEntity> {
        try {
            const shiftFromDb: IShiftDbEntity = await this.db.shifts
                    .where({id: shiftId, teamId: teamId}).first();
            return ShiftEntity.fromJson(shiftFromDb);
        } catch (error) {
            InstrumentationService.trackException(error, "getShiftFromDb");
        }
        return null;
    }

    /**
     * Fetch shifts in a team
     */
    public async getShifts(teamId: string, fetchStartTime: Moment, fetchEndTime: Moment): Promise<IShiftEntity[]> {
        if (!this.db) {
            return null;
        }
        let shifts: IShiftEntity[] = null;
        const fetchStartTimeStamp: number = fetchStartTime.valueOf();
        const fetchEndTimeStamp: number = fetchEndTime.valueOf();

        try {
            const shiftsFromDb: IShiftDbEntity[] = await this.db.shifts
                .where("[startTime+endTime+teamId]")
                // Use the key to do a more performant exclusion of items that don't overlap with the current fetch range.
                // With compound indexes, we can't really do complex logic filters as the filter() handler below, but we still
                // use the indexes here to partially weed out items that should excluded.
                .below([fetchEndTimeStamp, fetchEndTimeStamp, teamId]) // Only include items where (item.startTime < fetchEndTimeStamp)
                // Filter out items that do not overlap with the current fetch range
                .filter((shift: IShiftDbEntity) => { return shift.startTime < fetchEndTimeStamp && shift.endTime > fetchStartTimeStamp && shift.teamId === teamId; })
                .toArray();
            shifts = shiftsFromDb.map((shift: IShiftDbEntity) => ShiftEntity.fromJson(shift));
        } catch (error) {
            InstrumentationService.trackException(error, "getShiftsFromDb");
        }
        return shifts;
    }

    /**
     * Set Shifts in Database
     * @param teams teams list
     */
    public async setShifts(shifts: IShiftEntity[]): Promise<void> {
        if (!this.db || !shifts || !shifts.length) {
            return;
        }

        try {
            await this.db.shifts.bulkPut(shifts.map( (shift: IShiftEntity) => ShiftEntity.toDbModel(shift)));
        } catch (error) {
            InstrumentationService.trackException(error, "setShiftsInDb");
        }
    }

    /**
     * Delete shifts in a team
     * @param teamId ID of the team
     * @param shiftIds IDs of shifts to delete
     */
    public async deleteShifts(teamId: string, shiftIds: string[]): Promise<void> {
        if (!this.db || !shiftIds || !shiftIds.length) {
            return;
        }

        try {
            await this.db.transaction("rw", this.db.shifts, async () => {
                for (let i = 0; i < shiftIds.length; i++) {
                    // delete shifts
                    await this.db.shifts.where({teamId: teamId, id: shiftIds[i]}).delete();
                }
            });
        } catch (error) {
            InstrumentationService.trackException(error, "deleteShiftsInDb");
        }
    }

    /**
     * Atomically set Shifts (and the time range that's in sync for the session) in the Database
     * @param teamId Team ID
     * @param sessionId Current Session ID
     * @param updatedShifts List of updated shifts
     * @param deletedShiftIds List of IDs of shifts that were hard deleted
     * @param sessionShiftsCacheStartTimestamp Start of time range that's cached in current session
     * @param sessionShiftsCacheEndTimestamp End of timestamp that's cached in current session
     */
    public async setShiftsInTimeRange(teamId: string, sessionId: string, updatedShifts: IShiftEntity[], deletedShiftIds: string[], sessionShiftsCacheStartTimestamp: number, sessionShiftsCacheEndTimestamp: number): Promise<void> {
        if (!this.db) {
            return;
        }

        try {
            await this.db.transaction("rw", [this.db.shifts, this.db.shiftsSyncState], async () => {
                await this.setShifts(updatedShifts);
                await this.deleteShifts(teamId, deletedShiftIds);
                await this.setShiftsCacheTimeRange(teamId, sessionId, sessionShiftsCacheStartTimestamp, sessionShiftsCacheEndTimestamp);
            });
        } catch (error) {
            InstrumentationService.trackException(error, "setShiftsInTimeRangeInDb");
        }
    }

    /**
     * Set Shifts cache timerange in Database
     * @param teamId Team ID
     * @param sessionId Session ID
     * @param syncStateStartTimestamp Start time of the contiguous timerange that's in sync
     * @param syncStateEndTimestamp End time of the contiguous timerange that's in sync
     */
    public async setShiftsCacheTimeRange(teamId: string, sessionId: string, syncStateStartTimestamp: number, syncStateEndTimestamp: number): Promise<void> {
        if (!this.db) {
            return;
        }

        const shiftsSyncState: IItemsInTimeRangeSyncState = {
            sessionId: sessionId,
            teamId: teamId,
            syncStateStartTimestamp: syncStateStartTimestamp,
            syncStateEndTimestamp: syncStateEndTimestamp,
            lastUpdateTimestamp: moment().valueOf()
        };

        try {
            await this.db.shiftsSyncState.put(shiftsSyncState);
        } catch (error) {
            InstrumentationService.trackException(error, "setShiftsCacheTimeRangeInDb");
        }
    }

    /**
     * Get the shift sync state for a team in the current session
     * @param teamId Team ID
     * @param sessionId  Session ID
     */
    public async getShiftsSyncState(teamId: string, sessionId: string): Promise<IItemsInTimeRangeSyncState> {
        if (!this.db) {
            return null;
        }

        try {
            return await this.db.shiftsSyncState
                .where({sessionId: sessionId, teamId: teamId}).first();
        } catch (error) {
            InstrumentationService.trackException(error, "getShiftsSyncStateFromDb");
        }
        return null;
    }

    /**
     * Delete the shift sync state for the current session
     * @param sessionId  Session ID
     */
    public async deleteShiftsSyncState(sessionId: string): Promise<void> {
        if (!this.db) {
            return null;
        }

        try {
            await this.db.shiftsSyncState.where({sessionId: sessionId}).delete();
        } catch (error) {
            InstrumentationService.trackException(error, "deleteShiftsSyncStateInDb");
        }
    }

    /**
     * Returns true if storing & retrieving of shifts/notes/openshifts from IndexedDb is enabled
     */
    public static IsShiftsIndexedDbEnabled() {
        return ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableShiftsIndexedDb);
    }

    /**
     * Delete the sessions
     * @param sessionIds List of sessionIds to delete
     */
    public async deleteSessionData(sessionIds: string[]) {
        if (!this.db || !sessionIds || !sessionIds.length) {
            return;
        }
        try {
            for (const sessionId of sessionIds) {
                await this.db.shiftsSyncState.where({sessionId: sessionId}).delete();
            }
        } catch (error) {
            InstrumentationService.trackException(error, "deleteSessionDataFromDb");
        }
    }
}